import React, { Suspense } from 'react';

// Purposely split for more compact imports
import { Redirect, Route } from 'react-router';
import IndexRoute from 'react-router/lib/IndexRoute';
import IndexRedirect from 'react-router/lib/IndexRedirect';

import { CollectionViewLoadingIndicator } from '@repo/ui/components/ui/LoadingIndicator';
import AdaptiveExperience from '@/components/AdaptiveExperience';
import AppHandler from '@/components/AppContainer';
import CheckAgreements from '@/components/CheckAgreements';
import DiagnosticsHandler from '@/components/Diagnostics';
import DonationPage from '@/components/organization/Donate';
import FocusedExperience from '@/components/FocusedExperience';
import ForgotPasswordHandler from '@/components/user/ForgotPassword';
import LoginHandler from '@/components/user/Login';
import LogoutHandler from '@/components/Logout';
import ManagementExperience from '@/components/ManagementExperience';
import NotFoundHandler from '@/components/PageNotFound';
import OAuthAuthorize from '@/components/user/OAuthAuthorize';
import OptionalAuthHandler from '@/components/OptionalAuth';
import PlatformOfflineCheck from '@/components/PlatformOfflineCheck';
import RequireAuthHandler from '@/components/RequireAuth';
import ResetPasswordHandler from '@/components/user/ResetPassword';
import SignupHandler from '@/components/user/Signup';
import UnsubscribeHandler from '@/components/user/Unsubscribe';
import routes, { RoutePath } from './routeImports';

const RouteLoading = () => <CollectionViewLoadingIndicator />;

// Use direct return from routes mappings when SSR rendering, as it's already a resolved component
// Use Suspense and React.lazy on the client side.
const load = (key: RoutePath) => {
  if (import.meta.env.SSR) {
    return routes[key];
  } else {
    const Component = routes[key];
    return (props) => (
      <Suspense fallback={<RouteLoading />}>
        <Component {...props} />
      </Suspense>
    );
  }
};

// prettier-ignore
export default (
  <Route name="app" path="/" component={AppHandler}>

    {/* Fullscreen dedicated post page for use in oEmbed previews of stories, used when composing a story in the platform */}
    <Route name="post" path="/embed/post/:id" component={load(`@/components/social/SinglePostEmbed`)} />

    <Route component={PlatformOfflineCheck}>
      <Route component={FocusedExperience}>
        <Route name="signup" path="/signup" component={SignupHandler} />
        <Route name="login" path="/login" component={LoginHandler} />
        <Route name="logout" path="/logout" component={LogoutHandler} />

        <Route path="/registration/business/payment" component={load(`@/components/registration/BusinessUpgrade`)} />

        <Route path="/user/reset-password/:token" component={ResetPasswordHandler} />
        <Route path="/user/forgot-password" component={ForgotPasswordHandler} />
        <Route path="/unsubscribe/:token" component={UnsubscribeHandler} />
        <Route path="/donate" component={DonationPage} />
      </Route>

      <Route component={AdaptiveExperience}>
        <Route name="diagnostics" path="diagnostics" component={DiagnosticsHandler} />

        <Route name="publicProfile" path="/user/profile/:hash">
          <Route component={RequireAuthHandler}>
            <Route path="edit" component={load(`@/components/user/profile/Edit`)} />
            <Route path="add-position" component={load(`@/components/user/profile/AddPosition`)}>
              <IndexRoute component={load(`@/components/user/profile/add-position/ChooseType`)} />
              <Route path="formal">
                <IndexRoute component={load(`@/components/user/profile/add-position/formal`)} />
                <Route path=":organizationSlug" component={load(`@/components/user/profile/add-position/formal/Details`)} />
              </Route>
              <Route path="informal" component={load(`@/components/user/profile/add-position/Informal`)} />
            </Route>
          </Route>

          <Route component={load(`@/components/user/Profile`)}>
            <Route name="userVolunteerism" path="volunteerism" component={load(`@/components/user/profile/Volunteerism`)} />
            <Route name="userTestimonials" path="testimonials" component={load(`@/components/user/profile/Testimonials`)} />
            <Route name="userPosts" path="posts" component={load(`@/components/user/profile/Stories`)} />
            <Route component={RequireAuthHandler}>
              <Route path="submissions" component={load(`@/components/user/profile/Submissions`)} />
            </Route>
            <IndexRedirect to="volunteerism" />
          </Route>

        </Route>

        <Route name="businessProfile" path="business/:slug">
          <IndexRoute component={load(`@/components/business/Profile`)} />
          <Route path="employee-volunteerism" component={load(`@/components/business/EmployeeVolunteerism`)} />
          <Route path="organizations" component={load(`@/components/business/SupportedOrganizations`)} />
          <Route path="events" component={load(`@/components/business/Events`)} />
          <Route path="positions" component={load(`@/components/business/Positions`)} />
          <Route path="testimonials">
            <IndexRoute component={load(`@/components/business/Testimonials`)} />
            <Route name="businessTestimonial" path=":testimonialId" component={load(`@/components/business/ViewTestimonial`)} />
          </Route>
          <Route path="pledge" component={load(`@/components/business/Pledge`)} />
          <Route path="locations" component={load(`@/components/business/Locations`)} />
        </Route>

        <Route name="organizationProfile" path="organization/:slug">
          <IndexRoute component={load(`@/components/organization/Profile`)} />
          <Route path="employee-volunteerism" component={load(`@/components/organization/EmployeeVolunteerism`)} />
          <Route path="relationships" component={load(`@/components/organization/Relationships`)} />
          <Route path="events" component={load(`@/components/organization/Events`)} />
          <Route path="positions" component={load(`@/components/organization/Positions`)} />
          <Route path="locations" component={load(`@/components/organization/Locations`)} />
        </Route>

        <Route name="opportunityProfile" path="opportunity/:slug" component={load(`@/components/opportunities/View`)} />
        <Route name="eventProfile" path="event/:id" component={load(`@/components/events/Profile`)} />

        <Route name="opportunitiesBrowse" path="opportunities/browse" component={load(`@/components/opportunities/browse`)} />

        <Route path="community">
          <Route name="events" path="events" component={load(`@/components/community/Events`)} />
          <Route name="organizations" path="organizations" component={load(`@/components/community/Organizations`)} />
          <Route name="businesses" path="businesses" component={load(`@/components/community/Businesses`)} />
          <IndexRedirect to="organizations" />
        </Route>

        <Route component={OptionalAuthHandler}>
          <Route path="/organization/:organizationSlug/leave-testimonial/:businessSlug/:token" component={load(`@/components/business/LeaveInformalTestimonial`)} />
          <Route name="post" path="/post/:id" component={load(`@/components/social/SinglePost`)} />
          <Route path="/post/:postId/print" printMode component={load(`@/components/social/PrintPost`)} />

          <Route path="logout-of-proxy" component={load(`@/components/LogoutOfProxy`)} />
        </Route>

      </Route>

      <Route component={RequireAuthHandler}>

        <Route component={FocusedExperience}>

          <Route name="formsContext" path="/forms/business/:contextSlug" defaultSignup component={load(`@/components/forms/common/Context`)}>
            <Route path=":formSlug">
              <IndexRoute name="formView" component={load(`@/components/forms/common/View`)} />
              <Route path="thanks" component={load(`@/components/forms/common/Thanks`)} />
            </Route>
            <Route path="*" component={NotFoundHandler} />
          </Route>

          <Route name="formsContext" path="/forms/organization/:contextSlug" defaultSignup component={load(`@/components/forms/common/Context`)}>
            <Route path=":formSlug">
              <IndexRoute name="formView" component={load(`@/components/forms/common/View`)} />
              <Route path="thanks" component={load(`@/components/forms/common/Thanks`)} />
            </Route>
            <Route path="*" component={NotFoundHandler} />
          </Route>

          <Route path="/user/needs/city" component={load(`@/components/user/UnknownCity`)} />
          <Route path="/user/needs/canada-us" component={load(`@/components/user/CanadaUSOnly`)} />
          <Route path="/user/needs/verify-sso-email" component={load(`@/components/user/VerifySsoEmail`)} />
          <Route path="/user/welcome" component={load(`@/components/user/WelcomeWizard`)} />

          <Route name="registerBusiness" path="/registration/business/:step" defaultSignup component={load(`@/components/registration/Business`)} />
          <Route path="/registration/business/2/:slug" component={load(`@/components/registration/BusinessVideo`)} />
          <Route name="businessManagement" path="/registration/business/goal/:slug" component={load(`@/components/registration/BusinessGoal`)} />
          <Route path="/registration/business/3/:slug" component={load(`@/components/registration/BusinessPledge`)} />

          <Route path="/registration/business/payment" component={load(`@/components/registration/BusinessUpgrade`)}>
            <Route path="memberships/:packageId/:tierId/billing-info" defaultSignup component={load(`@/components/management/account/BillingInfo`)} />
            <Route name="businessManagement" path="thank-you/:slug/:transactionId" component={load(`@/components/management/account/ThankYou`)} />
            <IndexRoute component={NotFoundHandler} />
          </Route>

          <Redirect from="/registration/organization/1" to="/registration/organization" />
          <Route path="/registration/organization" defaultSignup component={load(`@/components/registration/Organization`)} />
          <Route name="organizationManagement" path="/registration/organization/goal/:slug" component={load(`@/components/registration/OrganizationGoal`)} />

          <Route name="viewInvite" path="view-invite/:token" defaultSignup component={load(`@/components/user/ViewInvite`)} />
          <Route name="managePrintSubmission" path="/:contextType/:contextSlug/manage/forms/submissions/:submissionId/print" component={load(`@/components/management/forms/submissions/PrintView`)} />

          <Route name="oauthAuthorize" path="/oauth/authorize" component={OAuthAuthorize} />

        </Route>

        <Route component={AdaptiveExperience}>

          <Route path="welcome" component={load(`@/components/user/Welcome`)} />

          <Route name="acceptInvite" path="accept-invite/:token" defaultSignup component={load(`@/components/user/AcceptInvite`)} />
          <Route name="dashboard" path="dashboard(/:feedMode)" component={load(`@/components/user/Dashboard`)} />

          <Route path="/post/:id/edit" component={load(`@/components/social/EditPost`)} />

          <Route path="/user/settings" component={load(`@/components/user/Settings`)} />
          <Route path="/user/log-hours" component={load(`@/components/user/log-time`)}>
            <IndexRoute component={load(`@/components/user/log-time/PositionPicker`)} />
            <Route path="thanks" component={load(`@/components/user/log-time/Thanks`)} />
            <Route path=":applicationSlug">
              <Route path="pick-shift" component={load(`@/components/user/log-time/ShiftPicker`)} />
              <Route path="pick-date" component={load(`@/components/user/log-time/DateLogger`)} />
              <Route path="shift/:shiftId/:startDate" component={load(`@/components/user/log-time/ShiftLogger`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
          </Route>

          <Route name="userMessageInbox" path="/user/messages" component={load(`@/components/user/messaging/Inbox`)} />
          <Route name="userMessageThread" path="/user/messages/thread/:id" component={load(`@/components/user/messaging/thread`)} />
          <Route name="userMessageThreadGroup" path="/user/messages/thread-group/:id/:signature" component={load(`@/components/user/messaging/thread`)} />

          <Route name="opportunityProfileApply" path="/opportunity/:slug/apply" component={load(`@/components/opportunities/Apply`)} />
          <Route name="opportunityProfileApply" path="/opportunity/:slug/apply/:applicationId/thanks" component={load(`@/components/opportunities/ApplyThanks`)} />
          <Route name="opportunityProfileApply" path="/opportunity/:slug/apply/shift/:shiftId" component={load(`@/components/opportunities/Apply`)} />
          <Route name="opportunityProfileApply" path="/opportunity/:slug/apply/shift/:shiftId/:applicationId/thanks" component={load(`@/components/opportunities/ApplyThanks`)} />
          <Route name="opportunityProfileApply" path="/opportunity/:slug/update/shift/:shiftId" component={load(`@/components/opportunities/UpdateAttendance`)} />
          <Route name="opportunityProfileApplySpecific" path="/opportunity/:slug/apply/:applicationId" component={load(`@/components/opportunities/Apply`)} />

          <Route path="/forms/submission/:id" component={load(`@/components/forms/user/Submission`)} />

          <Route path="/business/:contextSlug/team" component={load(`@/components/business/team/index`)}>
            <Route path="list" component={load(`@/components/business/team/Coworkers`)} />
            <Route path="activity-feed" component={load(`@/components/business/team/Feed`)} />
            <Route path="dashboard" component={load(`@/components/business/team/Dashboard`)} />
            <IndexRedirect to="list" />
          </Route>

          <Redirect from="/business/:slug/employees" to="/business/:slug/team/list" />
          <Redirect from="/business/:slug/dashboard/employee-dashboard" to="/business/:slug/team/dashboard" />

          <Route path="/staff">
            <IndexRoute component={load(`@/components/staff/Dashboard`)} />
            <Route name="staffDashboardUsers" path="users" component={load(`@/components/staff/users`)} />
            <Route name="staffDashboardOrgs" path="organizations" component={load(`@/components/staff/orgList`)} />
            <Route path="broadcast" component={load(`@/components/staff/MessageUsers`)} />
            <Route path="merge-organizations" component={load(`@/components/staff/mergeOrgs`)} />
            <Route path="invite-business" component={load(`@/components/staff/InviteBusiness`)} />
            <Route path="invite-organization" component={load(`@/components/staff/InviteOrganization`)} />
            <Route path="geo-report" component={load(`@/components/staff/geoReport`)} />
            <Route path="trend-page" component={load(`@/components/staff/trendPage/trendPage`)} />
            <Route path="push" component={load(`@/components/staff/PushBroadcast`)} />
            <Route path="short-url" component={load(`@/components/staff/ShortUrl`)} />
            <Route path="custom-reports" component={load(`@/components/staff/CustomReports`)} />
            <Route path="regen-reports" component={load(`@/components/staff/reports/RegenReport`)} />
            <Route path="diagnostics" component={load(`@/components/staff/StaffDiagnostics`)} />
            <Route path="city/add" component={load(`@/components/staff/CityAdd`)} />
            <Route path="city(/:id)" component={load(`@/components/staff/CityWrapper`)}>
              <Route path="edit" component={load(`@/components/staff/CityEdit`)} />
              <Route path="radius" component={load(`@/components/staff/CityRadius`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
            <Route path="business(/:slug)" component={load(`@/components/staff/BusinessWrapper`)}>
              <Route path="details" component={load(`@/components/staff/BusinessDetails`)} />
              <Route path="payment-method" component={load(`@/components/staff/PaymentMethod`)} />
              <Route path="stats" component={load(`@/components/staff/BusinessStats`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
            <Route path="organization(/:slug)" component={load(`@/components/staff/OrganizationWrapper`)}>
              <Route path="details" component={load(`@/components/staff/OrganizationDetails`)} />
              <Route path="payment-method" component={load(`@/components/staff/PaymentMethod`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
            <Route path="reports">
              <IndexRoute component={load(`@/components/staff/reports/List`)} />
              <Route path="editor/:id" component={load(`@/components/staff/reports/Editor`)} />
            </Route>
            <Route path="user(/:hash)">
              <Route path="details" component={load(`@/components/staff/UserDetails`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
            <Route path="media" component={load(`@/components/staff/media`)}>
              <Route path="list" component={load(`@/components/staff/media/List`)} />
              <Route path="create" component={load(`@/components/staff/media/Create`)} />
              <Route path=":id/summary" component={load(`@/components/staff/media/Summary`)} />
              <Route name="mediaSitePosts" path=":id/feed/:key" component={load(`@/components/staff/media/Feed`)} />
              <IndexRedirect to="list" />
            </Route>
            <Route path="promotion">
              <Route path="events" component={load(`@/components/staff/promotion/Events`)} />
              <Route path="positions" component={load(`@/components/staff/promotion/Positions`)} />
              <IndexRoute component={NotFoundHandler} />
            </Route>
          </Route>

        </Route>

        <Route component={CheckAgreements}>
          <Route component={ManagementExperience}>

            <Route name="businessManagement" path="/business/:slug/manage" component={load(`@/components/management/layout/Business`)}>

              <IndexRedirect to="home" />
              <Route path="home" component={load(`@/components/management/home/IndexBusiness`)}>
                <Route path="dashboard" component={load(`@/components/management/home/Business`)} />
                <Route path="account">
                  <IndexRoute component={load(`@/components/management/account`)} />
                  <Route path="memberships/:packageId/:tierId/billing-info" component={load(`@/components/management/account/BillingInfo`)} />
                  <Route path="thank-you/:unused/:transactionId" component={load(`@/components/management/account/ThankYou`)} />
                </Route>
                <IndexRedirect to="dashboard" />
              </Route>

              <Route name="businessWelcome" path="welcome" component={load(`@/components/management/onboarding/business`)}>
                <IndexRedirect to="profile" />
                <Route path="overview" component={load(`@/components/management/onboarding/business/StepOverview`)} />
                <Route path="profile" masterStep="profile" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="pledge" subStep="pledge" component={load(`@/components/management/onboarding/business/steps/Pledge`)} />
                  <Route path="logo" subStep="logo" component={load(`@/components/management/onboarding/business/steps/Logo`)} />
                  <Route path="causes" subStep="causes" component={load(`@/components/management/onboarding/business/steps/Causes`)} />
                  <Route path="about" subStep="about" component={load(`@/components/management/onboarding/business/steps/About`)} />
                  <Route path="about-all" subStep="aboutAll" component={load(`@/components/management/onboarding/business/steps/AboutCombined`)} />
                  <Route path="connection" subStep="addConnection" component={load(`@/components/management/onboarding/business/steps/AddConnection`)} />
                  <Route path="story" subStep="postStory" component={load(`@/components/management/onboarding/business/steps/AutoPostStory`)} />
                  <Route path="*" component={NotFoundHandler} />
                  <IndexRedirect to="pledge" />
                </Route>
                <Route path="collective" masterStep="collective" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="contributions" subStep="contributions" component={load(`@/components/management/onboarding/business/steps/Contributions`)} />
                  <IndexRedirect to="contributions" />
                </Route>
                <Route path="email-signatures" masterStep="email-signatures" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="add" component={load(`@/components/management/onboarding/business/steps/EmailSignatures`)} />
                  <IndexRedirect to="add" />
                </Route>
                <Route path="career-postings" masterStep="career-postings" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="add" component={load(`@/components/management/onboarding/business/steps/CareerPostings`)} />
                  <IndexRedirect to="add" />
                </Route>
                <Route path="share" masterStep="share" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="profile" component={load(`@/components/management/onboarding/business/steps/ShareProfile`)} />
                  <IndexRedirect to="profile" />
                </Route>
                <Route path="business-type" masterStep="business-type" component={load(`@/components/management/onboarding/business/stepset/PageTemplate`)}>
                  <Route path="choose" component={load(`@/components/management/onboarding/business/steps/BusinessType`)} />
                  <IndexRedirect to="choose" />
                </Route>
              </Route>

              <Route path="activity-feed" component={load(`@/components/management/activityfeed`)} />

              <Route path="social" component={load(`@/components/management/social`)}>
                <Route path="feed" component={load(`@/components/management/social/Feed`)} />
                <Route path="marketing" component={load(`@/components/management/marketing/Resources`)} />
                <Route path="drafts" component={load(`@/components/management/social/Drafts`)} />
                <Route path="scheduled" component={load(`@/components/management/social/Scheduled`)} />
                <Route path="approval" component={load(`@/components/management/social/ApproveContent`)} />
                <IndexRedirect to={'feed'} />
              </Route>

              <Route path="volunteerism" component={load(`@/components/management/opportunities`)}>
                <Route path="positions">
                  <Route path="list" component={load(`@/components/management/opportunities/List`)} />
                  <Route path="create(/:eventId)" component={load(`@/components/management/opportunities/Create`)} />
                  <Route path=":positionId">
                    <Route path="add-beneficiary" component={load(`@/components/management/opportunities/AddBeneficiary`)} />
                    <Route path="summary" component={load(`@/components/management/opportunities/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/opportunities/Edit`)} />
                    <Route path="shift">
                      <Route path="create" component={load(`@/components/management/opportunities/CreateShift`)} />
                      <Route path=":shiftId">
                        <Route path="summary" component={load(`@/components/management/opportunities/ShiftSummary`)} />
                        <Route path="edit" component={load(`@/components/management/opportunities/ShiftEdit`)} />
                        <IndexRedirect to={'summary'} />
                      </Route>
                    </Route>
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>
                <Route path="events">
                  <Route path="list" component={load(`@/components/management/events/List`)} />
                  <Route path="create" component={load(`@/components/management/events/Create`)} />
                  <Route path=":eventId">
                    <Route path="add-supporter" component={load(`@/components/management/events/AddSupporter`)} />
                    <Route path="add-beneficiary" component={load(`@/components/management/events/AddBeneficiary`)} />
                    <Route path="summary" component={load(`@/components/management/events/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/events/Edit`)} />
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route path="approve-applicants" component={load(`@/components/management/approvalqueues/ApproveApplicants`)} />
                <Route name="businessDashboardApproveHours" path="approve-hours" component={load(`@/components/management/approvalqueues/ApproveOrganizationOrBusinessHours`)} />

                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Volunteerism`)} />

                <IndexRedirect to={'positions'} />

              </Route>

              <Route path="invite">
                <Route path="organizations" component={load(`@/components/management/invites/Organizations`)} />
                <Route path="businesses" component={load(`@/components/management/invites/Businesses`)} />
                <Route path="employees" component={load(`@/components/management/invites/Employees`)} />
                <Route path="admins" component={load(`@/components/management/invites/Admins`)} />
                <Route path="members" component={load(`@/components/management/invites/Members`)} />
                <IndexRedirect to={'organizations'} />
              </Route>

              <Route path="employees" component={load(`@/components/management/employees`)}>
                <Route path="list" component={load(`@/components/management/employees/List`)} />
                <Route path="roles" component={load(`@/components/management/employees/roles`)} >
                  <Route path="list" component={load(`@/components/management/employees/roles/List`)} />
                  <Route path="create" component={load(`@/components/management/employees/roles/Create`)} />
                  <Route path=":roleId">
                    <Route path="summary" component={load(`@/components/management/employees/roles/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/employees/roles/Edit`)} />
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to="list" />
                </Route>
                <Route path="dashboard" component={load(`@/components/management/employees/dashboard/BusinessTeamDashboard`)} />
                <Route path="invite/employees" component={load(`@/components/management/invites/Employees`)} />
                <Route path="invite/admins" component={load(`@/components/management/invites/Admins`)} />
                <Route path="approval" component={load(`@/components/management/approvalqueues/ApproveTeam`)} />
                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Team`)} />
                <Route path=":relationshipId">
                  <Route path="summary" component={load(`@/components/management/employees/Summary`)} />
                  <IndexRedirect to={'summary'} />
                </Route>
                <IndexRedirect to={'list'} />
              </Route>

              <Route path="members" component={load(`@/components/management/members`)}>
                <Route path="list" component={load(`@/components/management/members/List`)} />
                <Route name="businessDashboardMembersSummary" path="dashboard" component={load(`@/components/management/members/dashboard/MemberDashboard`)} />
                <Route path="invite" component={load(`@/components/management/invites/Members`)} />
                <IndexRedirect to={'list'} />
              </Route>

              <Route path="relationships">
                <Route path="organizations" component={load(`@/components/management/relationships/business/IndexOrganizations`)}>
                  <Route path="testimonials" component={load(`@/components/management/relationships/business/Testimonials`)} />
                  <Route path="list" component={load(`@/components/management/relationships/business/Organizations`)} />
                  <Route path="add" component={load(`@/components/management/relationships/business/AddOrganization`)} />
                  <Route path="invite" component={load(`@/components/management/invites/Organizations`)} />
                  <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Community`)} />
                  <Route path=":id/:direction" component={load(`@/components/management/relationships/business/organizations/Details`)} />
                  <IndexRedirect to={'list'} />
                </Route>
                <Route path="businesses" component={load('@/components/management/relationships/business/IndexBusinesses')}>
                  <Route path="testimonials" component={load('@/components/management/relationships/business/businesses/Testimonials')} />
                  <Route path="widgets">
                    <IndexRoute component={load('@/components/management/relationships/business/businesses/Widgets')} />
                    <Route component={load('@/components/management/relationships/business/businesses/widgets/index')}>
                      <Route path="news" component={load('@/components/management/relationships/business/businesses/widgets/News')} />
                      <Route path="orgs-supported" component={load('@/components/management/relationships/business/businesses/widgets/Organizations')} />
                      <Route path="member-logos" component={load('@/components/management/relationships/business/businesses/widgets/Businesses')} />
                      <Route path="testimonials" component={load('@/components/management/relationships/business/businesses/widgets/Testimonials')} />
                      <Route path="impact" component={load('@/components/management/relationships/business/businesses/widgets/Ticker')} />
                    </Route>
                  </Route>
                  <Route path="news" component={load('@/components/management/relationships/business/businesses/News')} />
                  <Route path="list" component={load('@/components/management/relationships/business/businesses/List')} />
                  <Route path="add" component={load('@/components/management/relationships/business/AddBusiness')} />
                  <Route path="invite" component={load(`@/components/management/invites/Businesses`)} />
                  <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Business`)} />
                  <IndexRedirect to={'list'} />
                </Route>

                <Redirect from="affiliates" to="organizations" />
                <Redirect from="testimonials" to="organizations/testimonials" />
                <Redirect from=":id" to="organizations/:id/outbound" />
                <IndexRedirect to={'organizations'} />
              </Route>

              <Route path="profile" component={load(`@/components/management/profile`)}>
                <Route path="edit" component={load(`@/components/management/profile/business/Profile`)} />
                <Route path="addresses" component={load(`@/components/management/profile/Addresses`)} />
                <Route path="getlisted" component={load(`@/components/management/profile/BusinessGetListed`)} />
                <Route path="settings" component={load(`@/components/management/profile/business/Settings`)} />
                <IndexRedirect to={'edit'} />
              </Route>

              <Route path="pages" component={load(`@/components/management/pages`)}>
                <Route path="list" component={load(`@/components/management/pages/List`)} />
                <Route path="reports" component={() => false} />
                <Route path="create" component={load(`@/components/management/pages/Create`)} />
                <Route path=":pageIndexSlug">
                  <Route path=":pageSlug">
                    <Route path="builder" component={load(`@/components/management/pages/builder`)} />
                    <Route path="preview" component={load(`@/components/management/pages/Preview`)} />
                    <IndexRoute component={NotFoundHandler} />
                  </Route>
                  <IndexRoute component={NotFoundHandler} />
                </Route>
                <IndexRedirect to={'list'} />
              </Route>

              <Route path="forms" component={load(`@/components/management/forms`)}>
                <Route path="list" component={load(`@/components/management/forms/forms/List`)} />
                <Route path="create(/:parentId)" component={load(`@/components/management/forms/forms/Create`)} />

                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Forms`)} />

                <Route path="submissions">
                  <Route path="list">
                    <IndexRoute component={load(`@/components/management/forms/submissions/List`)} />
                    <Route path="custom-views">
                      <IndexRoute component={load(`@/components/management/forms/submissions/ListCustomViews`)} />
                      <Route path="manage" component={load(`@/components/management/forms/submissions/ManageViews`)} />
                      <Route path="create/template" component={load(`@/components/management/forms/submissions/CreateView`)} />
                      <Route path=":view" component={load(`@/components/management/forms/submissions/ListCustomViews`)} />
                    </Route>
                  </Route>
                  <Route path=":submissionId">
                    <Route path="summary" component={load(`@/components/management/forms/submissions/Summary`)} />
                    <Route path="create-story" component={load(`@/components/management/forms/submissions/CreatePost`)} />
                    <Route path="post/:postId" component={load(`@/components/management/forms/submissions/PostPreview`)} />
                    <IndexRedirect to={"summary"} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route path="owner-groups">
                  <Route path="list" component={load(`@/components/management/forms/ownerGroups/List`)} />
                  <Route path="create" component={load(`@/components/management/forms/ownerGroups/Create`)} />

                  <Route path=":ownerGroupId">
                    <Route path="edit" component={load(`@/components/management/forms/ownerGroups/Edit`)} />
                    <Route path="summary" component={load(`@/components/management/forms/ownerGroups/Summary`)} />
                    <IndexRoute component={NotFoundHandler} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route path="budgets">
                  <Route path="list" component={load(`@/components/management/forms/budgets/List`)} />
                  <Route path="create" component={load(`@/components/management/forms/budgets/Create`)} />
                  <Route path=":budgetId">
                    <Route path="summary" component={load(`@/components/management/forms/budgets/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/forms/budgets/Edit`)} />
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route path=":formSlug">
                  <Route path="builder" component={load(`@/components/management/forms/builder`)} />
                  <Route path="edit" component={load(`@/components/management/forms/forms/Edit`)} />
                  <Route path="summary" component={load(`@/components/management/forms/forms/Summary`)} />
                  <IndexRoute component={NotFoundHandler} />
                </Route>

                <IndexRedirect to={'list'} />
              </Route>

              <Route path="integrations" component={load(`@/components/management/integrations`)}>
                <Route path="facebook(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="instagram(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="linkedin(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="threads(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="rss" component={load(`@/components/management/integrations/rss`)} />
                <Route path="sso" component={load(`@/components/management/integrations/sso`)} />
                <Route path="widgets" component={load(`@/components/management/integrations/widgets`)}>
                  <Route path="ticker" component={load(`@/components/management/integrations/widgets/Ticker`)} />
                  <Route path="causes" component={load(`@/components/management/integrations/widgets/Causes`)} />
                  <Route path="testimonials" component={load(`@/components/management/integrations/widgets/Testimonials`)} />
                  <Route path="organizations" component={load(`@/components/management/integrations/widgets/Organizations`)} />
                  <Route path="news" component={load(`@/components/management/integrations/widgets/News`)} />
                  <Route path="events" component={load(`@/components/management/integrations/widgets/Events`)} />
                  <Route path="positions" component={load(`@/components/management/integrations/widgets/Positions`)} />
                  <IndexRedirect to={'news'} />
                </Route>
                <IndexRoute component={load(`@/components/management/integrations/list`)} />
              </Route>

              <Redirect from="marketing(/*)" to="social/marketing" />

              <Route path="*" component={NotFoundHandler} />
            </Route>

            <Route name="organizationManagement" path="/organization/:slug/manage" component={load(`@/components/management/layout/Organization`)}>

              <IndexRoute component={load(`@/components/management/home/Organization`)} />

              <Route name="organizationWelcome" path="welcome" component={load(`@/components/management/onboarding/organization`)}>
                <IndexRedirect to="profile" />
                <Route path="overview" component={load(`@/components/management/onboarding/organization/StepOverview`)} />
                <Route path="profile" masterStep="profile" component={load(`@/components/management/onboarding/organization/stepset/PageTemplate`)}>
                  <Route path="registered-id" subStep="charityId" component={load(`@/components/management/onboarding/organization/steps/RegisteredId`)} />
                  <Route path="logo" subStep="logo" component={load(`@/components/management/onboarding/organization/steps/Logo`)} />
                  <Route path="about" subStep="about" component={load(`@/components/management/onboarding/organization/steps/AboutCombined`)} />
                  <Route path="story" subStep="story" component={load(`@/components/management/onboarding/organization/steps/AutoPostStory`)} />
                  <Route path="connections" subStep="connections" component={load(`@/components/management/onboarding/organization/steps/AddConnection`)} />
                  <Route path="testimonials" subStep="testimonials" component={load(`@/components/management/onboarding/organization/steps/Testimonials`)} />
                  <Route path="*" component={NotFoundHandler} />
                  <IndexRedirect to="logo" />
                </Route>
              </Route>

              <Route path="social" component={load(`@/components/management/social`)}>
                <Route path="feed" component={load(`@/components/management/social/Feed`)} />
                <Route path="drafts" component={load(`@/components/management/social/Drafts`)} />
                <Route path="scheduled" component={load(`@/components/management/social/Scheduled`)} />
                <Route path="approval" component={load(`@/components/management/social/ApproveContent`)} />
                <IndexRedirect to={'feed'} />
              </Route>

              <Route path="volunteerism" component={load(`@/components/management/opportunities`)}>
                <Route path="positions">
                  <Route path="list" component={load(`@/components/management/opportunities/List`)} />
                  <Route path="create(/:eventId)" component={load(`@/components/management/opportunities/Create`)} />
                  <Route path=":positionId">
                    <Route path="add-beneficiary" component={load(`@/components/management/opportunities/AddBeneficiary`)} />
                    <Route path="summary" component={load(`@/components/management/opportunities/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/opportunities/Edit`)} />
                    <Route path="shift">
                      <Route path="create" component={load(`@/components/management/opportunities/CreateShift`)} />
                      <Route path=":shiftId">
                        <Route path="summary" component={load(`@/components/management/opportunities/ShiftSummary`)} />
                        <Route path="edit" component={load(`@/components/management/opportunities/ShiftEdit`)} />
                        <IndexRedirect to={'summary'} />
                      </Route>
                    </Route>
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>
                <Route path="events">
                  <Route path="list" component={load(`@/components/management/events/List`)} />
                  <Route path="create" component={load(`@/components/management/events/Create`)} />
                  <Route path=":eventId">
                    <Route path="add-supporter" component={load(`@/components/management/events/AddSupporter`)} />
                    <Route path="add-beneficiary" component={load(`@/components/management/events/AddBeneficiary`)} />
                    <Route path="summary" component={load(`@/components/management/events/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/events/Edit`)} />
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route name="organizationDashboardApproveHours" path="approve-hours" component={load(`@/components/management/approvalqueues/ApproveOrganizationOrBusinessHours`)} />
                <Route path="approve-applicants" component={load(`@/components/management/approvalqueues/ApproveApplicants`)} />
                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/organization/Volunteerism`)} />

                <IndexRedirect to={'positions'} />
              </Route>

              <Route path="invite">
                <Route path="employees" component={load(`@/components/management/invites/Employees`)} />
                <Route path="admins" component={load(`@/components/management/invites/Admins`)} />
                <Route path="organizations" component={load(`@/components/management/invites/Organizations`)} />
                <Route path="businesses" component={load(`@/components/management/invites/Businesses`)} />
                <IndexRedirect to={'employees'} />
              </Route>

              <Route path="employees" component={load(`@/components/management/employees`)}>
                <Route path="list" component={load(`@/components/management/employees/List`)} />
                <Route path="roles" component={load(`@/components/management/employees/roles`)} >
                  <Route path="list" component={load(`@/components/management/employees/roles/List`)} />
                  <Route path="create" component={load(`@/components/management/employees/roles/Create`)} />
                  <Route path=":roleId">
                    <Route path="summary" component={load(`@/components/management/employees/roles/Summary`)} />
                    <Route path="edit" component={load(`@/components/management/employees/roles/Edit`)} />
                    <IndexRedirect to={'summary'} />
                  </Route>
                  <IndexRedirect to="list" />
                </Route>
                <Route path="invite/employees" component={load(`@/components/management/invites/Employees`)} />
                <Route path="invite/admins" component={load(`@/components/management/invites/Admins`)} />
                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/business/Team`)} />
                <Route path=":relationshipId">
                  <Route path="summary" component={load(`@/components/management/employees/Summary`)} />
                  <IndexRedirect to={'summary'} />
                </Route>
                <IndexRedirect to={'list'} />
              </Route>

              <Route path="volunteers" component={load(`@/components/management/volunteers`)}>
                <Route path="list" component={load(`@/components/management/volunteers/List`)} />
                <Route path="recruit" component={load(`@/components/management/volunteers/FindVolunteers`)} />
                <Route name="organizationDashboardRecognize" path="recognize" component={load(`@/components/management/approvalqueues/ApproveEndorsementRequests`)} />
                <Route path="invite" component={load(`@/components/management/invites/Contributors`)} />

                <Route path="dashboard" component={load(`@/components/management/volunteers/dashboard/OrganizationDashboard`)}>
                  <Route path="volunteers" component={load(`@/components/management/volunteers/dashboard/Volunteers`)} />
                  <IndexRedirect to={'volunteers'} />
                </Route>

                <IndexRedirect to={'list'} />
              </Route>

              <Route path="relationships">
                <Route path="businesses" component={load(`@/components/management/relationships/organization/IndexBusinesses`)}>
                  <Route path="testimonials" component={load(`@/components/management/relationships/organization/Testimonials`)} />
                  <Route path="testimonial-requests" component={load(`@/components/management/relationships/organization/TestimonialRequests`)} />
                  <Route path="list" component={load(`@/components/management/relationships/organization/Businesses`)} />
                  <Route path="add" component={load(`@/components/management/relationships/organization/AddBusiness`)} />
                  <Route path="invite" component={load(`@/components/management/invites/Businesses`)} />
                  <IndexRedirect to={'list'} />
                </Route>
                <Route path="organizations" component={load(`@/components/management/relationships/organization/IndexOrganizations`)}>
                  <Route path="list" component={load(`@/components/management/relationships/organization/Organizations`)} />
                  <Route path="add" component={load(`@/components/management/relationships/organization/AddOrganization`)} />
                  <IndexRedirect to={'list'} />
                </Route>
                <Route path=":id" component={load(`@/components/management/relationships/Details`)} />
                <Redirect from="testimonials" to="businesses/testimonials" />
                <Redirect from="affiliates" to="businesses" />
                <IndexRedirect to={'businesses'} />
              </Route>

              <Route path="profile" component={load(`@/components/management/profile`)}>
                <Route path="edit" component={load(`@/components/management/profile/Organization`)} />
                <Route path="addresses" component={load(`@/components/management/profile/Addresses`)} />

                <Route path="relationships">
                  <Route path="organizations" component={load(`@/components/management/relationships/organization/IndexOrganizations`)}>
                    <Route path="list" component={load(`@/components/management/relationships/organization/Organizations`)} />
                    <Route path="add" component={load(`@/components/management/relationships/organization/AddOrganization`)} />
                    <IndexRedirect to={'list'} />
                  </Route>
                  <Route path=":id" component={load(`@/components/management/relationships/Details`)} />
                  <IndexRedirect to={'organizations'} />
                </Route>

                <IndexRedirect to={'edit'} />
              </Route>

              <Route path="pages" component={load(`@/components/management/pages`)}>
                <Route path="list" component={load(`@/components/management/pages/List`)} />
                <Route path="reports" component={() => false} />
                <Route path="create" component={load(`@/components/management/pages/Create`)} />
                <Route path=":pageIndexSlug">
                  <Route path=":pageSlug">
                    <Route path="builder" component={load(`@/components/management/pages/builder`)} />
                    <Route path="preview" component={load(`@/components/management/pages/Preview`)} />
                    <IndexRoute component={NotFoundHandler} />
                  </Route>
                </Route>
                <IndexRedirect to={'list'} />
              </Route>

              <Route path="forms" component={load(`@/components/management/forms`)}>
                <Route path="list" component={load(`@/components/management/forms/forms/List`)} />
                <Route path="create(/:parentId)" component={load(`@/components/management/forms/forms/Create`)} />

                <Route path="reports(/:reportId)" component={load(`@/components/management/reports/organization/Forms`)} />

                <Route path="submissions">
                  <Route path="list" >
                    <IndexRoute component={load(`@/components/management/forms/submissions/List`)} />
                    <Route path="custom-views">
                      <IndexRoute component={load(`@/components/management/forms/submissions/ListCustomViews`)} />
                      <Route path="manage" component={load(`@/components/management/forms/submissions/ManageViews`)} />
                      <Route path="create/template" component={load(`@/components/management/forms/submissions/CreateView`)} />
                      <Route path=":view" component={load(`@/components/management/forms/submissions/ListCustomViews`)} />
                    </Route>
                  </Route>
                  <Route path=":submissionId">
                    <Route path="summary" component={load(`@/components/management/forms/submissions/Summary`)} />
                    <Route path="create-story" component={load(`@/components/management/forms/submissions/CreatePost`)} />
                    <Route path="post/:postId" component={load(`@/components/management/forms/submissions/PostPreview`)} />
                    <IndexRedirect to={"summary"} />
                  </Route>
                  <IndexRedirect to={'list'} />
                </Route>

                <Route path=":formSlug">
                  <Route path="builder" component={load(`@/components/management/forms/builder`)} />
                  <Route path="edit" component={load(`@/components/management/forms/forms/Edit`)} />
                  <Route path="summary" component={load(`@/components/management/forms/forms/Summary`)} />
                  <IndexRoute component={NotFoundHandler} />
                </Route>

                <IndexRedirect to={'list'} />
              </Route>


              <Route path="integrations" component={load(`@/components/management/integrations`)}>
                <Route path="facebook(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="instagram(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="linkedin(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="threads(/:serviceId)" component={load(`@/components/management/integrations/settings`)} />
                <Route path="rss" component={load(`@/components/management/integrations/rss`)} />
                <Route path="widgets" component={load(`@/components/management/integrations/widgets`)}>
                  <Route path="businesses" component={load(`@/components/management/integrations/widgets/Businesses`)} />
                  <Route path="news" component={load(`@/components/management/integrations/widgets/News`)} />
                  <Route path="events" component={load(`@/components/management/integrations/widgets/Events`)} />
                  <Route path="positions" component={load(`@/components/management/integrations/widgets/Positions`)} />
                  <IndexRedirect to={'news'} />
                </Route>
                <IndexRoute component={load(`@/components/management/integrations/list`)} />
              </Route>

              <Route path="*" component={NotFoundHandler} />
            </Route>

          </Route>
        </Route>

        <IndexRedirect to="/dashboard" />

      </Route>
    </Route>

    <IndexRedirect to="/dashboard" />
    <Route path="*" component={NotFoundHandler} />

  </Route>
);
